import { apiUrl, currentUserId, currentUserQuery } from "./auth";

export type SuiviPerson = { id: number; name: string };
export type SuiviTask = { id: number; title: string; due_on: string; assignee_id: number; kind: TaskKind; done: boolean; note_key?: string; sequence_code?: string; remind_time?: string };
export type RelationStatus = "prospect" | "client" | "former";
export type PipelineStage = { code: string; label: string; color: string; kind: "open" | "won" | "lost" };
export type Stage = string;

export const STAGES: PipelineStage[] = [
  { code: "scheduled", label: "RDV programmé", color: "#2563eb", kind: "open" },
  { code: "confirmed", label: "RDV confirmé", color: "#0891b2", kind: "open" },
  { code: "proposal", label: "Proposition envoyée", color: "#0f766e", kind: "open" },
  { code: "negotiation", label: "Négociation", color: "#7c3aed", kind: "open" },
  { code: "won", label: "Gagnée", color: "#059669", kind: "won" },
  { code: "lost", label: "Perdue", color: "#e11d48", kind: "lost" },
];

export const STAGE_COLORS = ["#2563eb", "#0891b2", "#0f766e", "#7c3aed", "#d97706", "#ea580c", "#059669", "#e11d48"];

const LEGACY: Record<string, string> = {
  new: "scheduled",
  contacted: "scheduled",
  rdv: "scheduled",
};

export function stageList(stages?: PipelineStage[]): PipelineStage[] {
  return stages && stages.length > 0 ? stages : STAGES;
}

export function canonStage(status: string, stages: PipelineStage[] = STAGES): string {
  const code = LEGACY[status] || status;
  return stageList(stages).some((item) => item.code === code) ? code : (stageList(stages).find((item) => item.kind === "open")?.code || "scheduled");
}

export function nextStage(status: string, stages: PipelineStage[] = STAGES): string | null {
  const flow = stageList(stages).filter((item) => item.kind !== "lost");
  const index = flow.findIndex((item) => item.code === canonStage(status, stages));
  if (index < 0 || index >= flow.length - 1) return null;
  return flow[index + 1].code;
}

export function prevStage(status: string, stages: PipelineStage[] = STAGES): string | null {
  const list = stageList(stages);
  const lost = list.find((item) => item.kind === "lost")?.code;
  const flow = list.filter((item) => item.kind !== "lost");
  if (status === lost) {
    const open = flow.filter((item) => item.kind === "open");
    return open[open.length - 1]?.code || null;
  }
  const index = flow.findIndex((item) => item.code === canonStage(status, stages));
  if (index <= 0) return null;
  return flow[index - 1].code;
}

export function stageOf(code: string, stages: PipelineStage[] = STAGES): PipelineStage | undefined {
  return stageList(stages).find((item) => item.code === code);
}
export type TaskKind = "call" | "quote" | "unpaid" | "meeting";
export type SuiviDeal = { title: string; amount: number; status: Stage; quote_id: number; probability: number; expected_on: string; loss_reason: string };
export type SuiviPast = { id: number; title: string; amount: number };
export type Suivi = {
  people: SuiviPerson[];
  owner_user_id: number;
  relation_status: RelationStatus;
  deal: SuiviDeal;
  history?: SuiviPast[];
  tasks: SuiviTask[];
  feed: { body: string; actor_name?: string; created_at: string }[];
  stages?: PipelineStage[];
};

function headers(json = false): HeadersInit {
  return {
    ...(currentUserId() ? { "X-User-Id": currentUserId() } : {}),
    ...(json ? { "Content-Type": "application/json" } : {}),
  };
}

async function read(res: Response): Promise<Suivi> {
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; suivi?: Suivi };
  if (!res.ok || data.success === false || !data.suivi) {
    throw new Error(data.message || "Suivi indisponible");
  }
  return data.suivi;
}

export function loadMarks(): Promise<{ id: number; relation_status: RelationStatus }[]> {
  const query = currentUserQuery();
  return fetch(`${apiUrl("suivi.php")}${query}${query ? "&" : "?"}marks=1`, {
    credentials: "include",
    cache: "no-store",
    headers: headers(),
  }).then(async (res) => {
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; marks?: { id: number; relation_status: RelationStatus }[] };
    if (!res.ok || data.success === false) return [];
    return data.marks || [];
  });
}

export function loadSuivi(fiche: number): Promise<Suivi> {
  const query = currentUserQuery();
  return fetch(`${apiUrl("suivi.php")}${query}${query ? "&" : "?"}fiche=${fiche}`, {
    credentials: "include",
    cache: "no-store",
    headers: headers(),
  }).then(read);
}

export function saveSuivi(fiche: number, body: Record<string, unknown>): Promise<Suivi> {
  return fetch(apiUrl("suivi.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: headers(true),
    body: JSON.stringify({ fiche, ...body }),
  }).then(read);
}

export async function saveStages(stages: PipelineStage[]): Promise<PipelineStage[]> {
  const res = await fetch(apiUrl("stages.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: headers(true),
    body: JSON.stringify({ stages }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; stages?: PipelineStage[] };
  if (!res.ok || data.success === false || !data.stages) {
    throw new Error(data.message || "Étapes non enregistrées");
  }
  window.dispatchEvent(new Event("crm-suivi-refresh"));
  return data.stages;
}
